import React, { useEffect, useRef, useState } from "react";
import "./Marketing.css";
import marketingVideo from "../../assets/marketingvideo.mp4";

const Marketing = () => {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(false);
  const [showOverlay, setShowOverlay] = useState(true);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent));
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setMuted(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = muted;
      if (videoRef.current.paused) {
        videoRef.current.play().catch((e) => console.log(e));
      }
    }
  }, [muted]);

  // Fade out the overlay text after 4 seconds (only on desktop)
  useEffect(() => {
    if (!isMobile) {
      const timer = setTimeout(() => {
        setShowOverlay(false);
      }, 4000);
      return () => clearTimeout(timer);
    } else {
      // On mobile, keep overlay visible but allow scrolling
      setShowOverlay(true);
    }
  }, [isMobile]);

  useEffect(() => {
    const sections = document.querySelectorAll(".animate-on-scroll");
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
          }
        });
      },
      { threshold: 0.1 }
    );

    sections.forEach((section) => observer.observe(section));
    return () => sections.forEach((section) => observer.unobserve(section));
  }, []);

  return (
    <div className="marketing-page">
      {/* Video Section */}
      <div 
        className="video-wrapper"
        onMouseEnter={() => !isMobile && setShowOverlay(true)}
        onMouseLeave={() => !isMobile && setShowOverlay(false)}
      >
        <video
          ref={videoRef}
          src={marketingVideo}
          className="marketing-video"
          autoPlay
          loop
          muted={muted}
          playsInline
          preload="auto"
        />
        <div className={`video-overlay ${showOverlay ? "visible" : "hidden"}`}>
          <div className="digital-hero-content">
            <h1 className="animate-on-scroll">Digital Marketing Solutions</h1>
            <p className="animate-on-scroll">
              Data-driven strategies to grow your brand, increase traffic, and drive real conversions in the digital space.
            </p>
            <a href="/contact-us" className="primary-cta animate-on-scroll">
              Get Your Strategy
            </a>
          </div>
        </div>
      </div>

      {/* Services Section */}
      <section className="digital-services-section animate-on-scroll">
        <div className="section-header">
          <h2 className="section-title">Our Digital Expertise</h2>
          <p className="section-subtitle">
            Comprehensive digital marketing solutions designed to elevate your brand and drive measurable results
          </p>
        </div>
        <div className="digital-services-grid">
          <div className="service-card">
            <div className="service-number">01</div>
            <h3>SEO</h3>
            <p>Technical SEO, on-page optimization, and content strategy to rank higher and drive organic traffic.</p>
          </div>
          <div className="service-card">
            <div className="service-number">02</div>
            <h3>Performance Marketing</h3>
            <p>Targeted campaigns on Google Ads, Meta (Facebook/Instagram), TikTok, and LinkedIn to maximize ROI.</p>
          </div>
          <div className="service-card">
            <div className="service-number">03</div>
            <h3>Social Media Management</h3>
            <p>Strategic content planning, community management, and analytics to build a loyal audience.</p>
          </div>
          <div className="service-card">
            <div className="service-number">04</div>
            <h3>Content Marketing</h3>
            <p>Compelling copywriting for blogs, landing pages, and ads that engages users and converts leads.</p>
          </div>
          <div className="service-card">
            <div className="service-number">05</div>
            <h3>Email & CRM</h3>
            <p>Automated funnels, personalized email campaigns, and customer retention strategies.</p>
          </div>
          <div className="service-card">
            <div className="service-number">06</div>
            <h3>CRO & Analytics</h3>
            <p>Conversion Rate Optimization and in-depth reporting to turn visitors into paying customers.</p>
          </div>
          <div className="service-card">
            <div className="service-number">07</div>
            <h3>Web Optimization</h3>
            <p>Enhancing website performance, speed, and user experience for better search rankings and conversion.</p>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="digital-process-section animate-on-scroll">
        <div className="section-header">
          <h2 className="section-title">How We Work</h2>
          <p className="section-subtitle">
            A proven methodology that transforms your digital presence into measurable business growth
          </p>
        </div>
        <div className="process-container">
          <div className="process-steps">
            <div className="process-step">
              <div className="step-number-wrapper">
                <div className="step-number">01</div>
              </div>
              <div className="step-content">
                <h3>Discovery</h3>
                <p>We analyze your business, audience, and competitors to understand your unique market position.</p>
              </div>
            </div>
            <div className="process-step">
              <div className="step-number-wrapper">
                <div className="step-number">02</div>
              </div>
              <div className="step-content">
                <h3>Strategy</h3>
                <p>We build a tailored digital roadmap aligned with your business objectives and KPIs.</p>
              </div>
            </div>
            <div className="process-step">
              <div className="step-number-wrapper">
                <div className="step-number">03</div>
              </div>
              <div className="step-content">
                <h3>Execution</h3>
                <p>We launch campaigns and optimize content across all relevant digital channels.</p>
              </div>
            </div>
            <div className="process-step">
              <div className="step-number-wrapper">
                <div className="step-number">04</div>
              </div>
              <div className="step-content">
                <h3>Optimization</h3>
                <p>Continuous testing and refining to improve performance and maximize ROI.</p>
              </div>
            </div>
            <div className="process-step">
              <div className="step-number-wrapper">
                <div className="step-number">05</div>
              </div>
              <div className="step-content">
                <h3>Reporting</h3>
                <p>Transparent reports showing measurable growth, insights, and actionable recommendations.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Metrics/Proof Section */}
      <section className="digital-metrics-section animate-on-scroll">
        <div className="metrics-container">
          <div className="metric-box">
            <div className="metric-accent"></div>
            <h3>Measurable ROI</h3>
            <p>Focus on Return on Ad Spend (ROAS) with transparent reporting and clear performance metrics</p>
          </div>
          <div className="metric-box">
            <div className="metric-accent"></div>
            <h3>Data-Driven</h3>
            <p>Decisions based on real-time analytics and comprehensive market intelligence</p>
          </div>
          <div className="metric-box">
            <div className="metric-accent"></div>
            <h3>Growth Focused</h3>
            <p>Scalable strategies designed for long-term success and sustainable business growth</p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="digital-faq-section animate-on-scroll">
        <h2 className="section-title">Frequently Asked Questions</h2>
        <div className="faq-grid">
          <div className="faq-item">
            <h4>How long does it take to see results from SEO?</h4>
            <p>SEO is a long-term strategy. Typically, noticeable improvements in rankings and traffic can be seen within 3 to 6 months, depending on competition and current site health.</p>
          </div>
          <div className="faq-item">
            <h4>Which social media platforms should my business be on?</h4>
            <p>It depends on your target audience. B2B businesses often thrive on LinkedIn, while B2C brands may find more success on Instagram, TikTok, or Facebook.</p>
          </div>
          <div className="faq-item">
            <h4>What is the difference between SEO and SEM?</h4>
            <p>SEO (Search Engine Optimization) focuses on organic (free) traffic growth, while SEM (Search Engine Marketing) involves paid advertising to get immediate visibility.</p>
          </div>
          <div className="faq-item">
            <h4>Do you provide monthly reports?</h4>
            <p>Yes, we provide detailed monthly reports covering key metrics, campaign performance, and actionable insights for the next steps.</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Marketing;
